import api from "../api";
import { AdminUser, AdminAvailableIntegration, AdminChatBotProfile, AdminFolderAccess, AdminUsersResponse, AdminUserIntegrations, AdminVendorConnection, AdminUserCreateResponse } from "types/interfaces/adminUsers";

export interface GetAdminUsersParams {
  page?: number;
  search?: string;
  page_size?: number;
  role?: string;
  sort_by?: "name" | "email" | "date_joined" | "last_login" | "folders" | "integrations";
  sort_order?: "asc" | "desc";
}

export interface UpdateAdminUserPayload {
  id: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  is_web_user?: boolean;
  is_calibrate_admin?: boolean;
  is_integration_user?: boolean;
  is_chat_bot_allowed?: boolean;
  is_pass_reset?: boolean;
  is_bot_manager?: boolean;
  is_integration_manager?: boolean;
  is_sftp_manager?: boolean;
  is_user_and_folder_manager?: boolean;
  is_delete_manager?: boolean;
}

export interface CreateAdminUserPayload {
  username: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  is_web_user?: boolean;
  is_calibrate_admin?: boolean;
  is_integration_user?: boolean;
  is_chat_bot_allowed?: boolean;
}

const adminUsersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAdminUsers: builder.query<AdminUsersResponse, GetAdminUsersParams>({
      query: ({ page = 1, search, page_size, role, sort_by, sort_order } = {}) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        if (search) params.set("search", search);
        if (page_size) params.set("page_size", String(page_size));
        if (role) params.set("role", role);
        if (sort_by) params.set("sort_by", sort_by);
        if (sort_order) params.set("sort_order", sort_order);
        return `/api/admin/users/?${params.toString()}`;
      },
      providesTags: ["AdminUsers"],
    }),

    updateAdminUser: builder.mutation<AdminUser, UpdateAdminUserPayload>({
      query: ({ id, ...body }) => ({
        url: `/api/admin/users/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    createAdminUser: builder.mutation<AdminUserCreateResponse, CreateAdminUserPayload>({
      query: (body) => ({
        url: `/api/admin/users/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    resetAdminUserPassword: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `/api/admin/users/${id}/reset-password/`,
        method: "POST",
      }),
    }),

    deleteAdminUser: builder.mutation<
      { message: string; s3_cleanup_queued: boolean; job_id?: string },
      number
    >({
      query: (id) => ({
        url: `/api/admin/users/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    getAdminUser: builder.query<AdminUser, number>({
      query: (id) => `/api/admin/users/${id}/`,
      providesTags: (_result, _error, id) => [{ type: "AdminUsers", id }],
    }),

    getS3CleanupStatus: builder.query<
      { job_id: string; status: string; error_message: string | null },
      string
    >({
      query: (jobId) => `/api/admin/s3-cleanup-status/${jobId}/`,
    }),

    getAdminUserFolders: builder.query<AdminFolderAccess[], number>({
      query: (id) => `/api/admin/users/${id}/folders/`,
      providesTags: (_result, _error, id) => [{ type: "AdminUsers", id: `folders-${id}` }],
    }),

    /** Folders not owned by this user, with has_access. For Share Folder Access modal. */
    getAdminUserFoldersToShare: builder.query<AdminFolderAccess[], number>({
      query: (id) => `/api/admin/users/${id}/folders/?exclude_owned=1`,
      providesTags: (_result, _error, id) => [{ type: "AdminUsers", id: `folders-${id}` }],
    }),

    getAdminUserOwnedFolders: builder.query<AdminFolderAccess[], number>({
      query: (id) => `/api/admin/users/${id}/folders/owned/`,
      providesTags: (_result, _error, id) => [{ type: "AdminUsers", id: `folders-owned-${id}` }],
    }),

    getAdminUserSharedFolders: builder.query<AdminFolderAccess[], number>({
      query: (id) => `/api/admin/users/${id}/folders/shared/`,
      providesTags: (_result, _error, id) => [{ type: "AdminUsers", id: `folders-shared-${id}` }],
    }),

    grantFolderAccess: builder.mutation<void, { userId: number; folderId: number }>({
      query: ({ userId, folderId }) => ({
        url: `/api/admin/users/${userId}/folders/${folderId}/grant/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `folders-${userId}` },
        { type: "AdminUsers", id: `folders-owned-${userId}` },
        { type: "AdminUsers", id: `folders-shared-${userId}` },
        "AdminUsers",
      ],
    }),

    revokeFolderAccess: builder.mutation<void, { userId: number; folderId: number }>({
      query: ({ userId, folderId }) => ({
        url: `/api/admin/users/${userId}/folders/${folderId}/revoke/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `folders-${userId}` },
        { type: "AdminUsers", id: `folders-owned-${userId}` },
        { type: "AdminUsers", id: `folders-shared-${userId}` },
        "AdminUsers",
      ],
    }),

    getAdminUserIntegrations: builder.query<AdminUserIntegrations, number>({
      query: (id) => `/api/admin/users/${id}/integrations/`,
      providesTags: (_result, _error, id) => [{ type: "AdminUsers", id: `integrations-${id}` }],
    }),

    updateAdminConnection: builder.mutation<AdminVendorConnection, { connId: number; host?: string; username?: string; port?: string; client_code?: string; password?: string; passphrase?: string; key?: string; access_token?: string }>({
      query: ({ connId, ...body }) => ({
        url: `/api/admin/connections/${connId}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["AdminUsers"],
    }),

    toggleIntegrationEnabled: builder.mutation<{ id: number; enabled: boolean }, { matchId: number; userId: number }>({
      query: ({ matchId }) => ({
        url: `/api/admin/matches/${matchId}/toggle/`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `integrations-${userId}` },
      ],
    }),

    generateAdminUserPassword: builder.mutation<{ generated_password: string }, number>({
      query: (id) => ({
        url: `/api/admin/users/${id}/generate-password/`,
        method: "POST",
      }),
    }),

    getAvailableIntegrations: builder.query<{ integrations: AdminAvailableIntegration[] }, number>({
      query: (userId) => `/api/admin/users/${userId}/integrations/available/`,
      providesTags: (_result, _error, userId) => [{ type: "AdminUsers", id: `available-integrations-${userId}` }],
    }),

    grantIntegrationAccess: builder.mutation<void, { userId: number; matchId: number }>({
      query: ({ userId, matchId }) => ({
        url: `/api/admin/users/${userId}/integrations/${matchId}/grant/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `integrations-${userId}` },
        { type: "AdminUsers", id: `available-integrations-${userId}` },
      ],
    }),

    revokeIntegrationAccess: builder.mutation<void, { userId: number; matchId: number }>({
      query: ({ userId, matchId }) => ({
        url: `/api/admin/users/${userId}/integrations/${matchId}/revoke/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `integrations-${userId}` },
        { type: "AdminUsers", id: `available-integrations-${userId}` },
      ],
    }),

    getAdminChatBotProfile: builder.query<AdminChatBotProfile, number>({
      query: (userId) => `/api/admin/users/${userId}/chat-bot-profile/`,
      providesTags: (_result, _error, userId) => [{ type: "AdminUsers", id: `chat-bot-profile-${userId}` }],
    }),

    createAdminChatBotProfile: builder.mutation<AdminChatBotProfile, { userId: number; chat_id?: string; api_key?: string; name?: string; email?: string }>({
      query: ({ userId, ...body }) => ({
        url: `/api/admin/users/${userId}/chat-bot-profile/`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `chat-bot-profile-${userId}` },
        "AdminUsers",
      ],
    }),

    updateAdminChatBotProfile: builder.mutation<AdminChatBotProfile, { userId: number; chat_id?: string; api_key?: string }>({
      query: ({ userId, ...body }) => ({
        url: `/api/admin/users/${userId}/chat-bot-profile/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "AdminUsers", id: `chat-bot-profile-${userId}` },
      ],
    }),
  }),
});

export const {
  useGetAdminUsersQuery,
  useUpdateAdminUserMutation,
  useCreateAdminUserMutation,
  useResetAdminUserPasswordMutation,
  useDeleteAdminUserMutation,
  useLazyGetS3CleanupStatusQuery,
  useGetAdminUserFoldersQuery,
  useGetAdminUserFoldersToShareQuery,
  useGetAdminUserOwnedFoldersQuery,
  useGetAdminUserSharedFoldersQuery,
  useGrantFolderAccessMutation,
  useRevokeFolderAccessMutation,
  useGetAdminUserIntegrationsQuery,
  useUpdateAdminConnectionMutation,
  useToggleIntegrationEnabledMutation,
  useGenerateAdminUserPasswordMutation,
  useGetAvailableIntegrationsQuery,
  useGrantIntegrationAccessMutation,
  useRevokeIntegrationAccessMutation,
  useGetAdminChatBotProfileQuery,
  useCreateAdminChatBotProfileMutation,
  useUpdateAdminChatBotProfileMutation,
  useGetAdminUserQuery,
} = adminUsersApi;
